import React, { useState, useEffect } from "react";
import styled from "styled-components/macro";

import { Helmet } from "react-helmet-async";

import {
  Avatar,
  Button as MuiButton,
  Card as MuiCard,
  CardContent as MuiCardContent,
  FormControl as MuiFormControl,
  Grid,
  TextField as MuiTextField,
  Typography as MuiTypography,
  Select,
  InputLabel,
  MenuItem,
  Box,
} from "@material-ui/core";

import Swal from "sweetalert2";

import { CloudUpload as MuiCloudUpload } from "@material-ui/icons";
import { CreditCard, Settings as SettingsIcon, Shield } from "react-feather";

import {
  updateInfoProfile,
  getClientBankInfo,
} from "../../services/profileSettingService";
import LocalizationPreferences from "../../components/LocalizationPreferences";

const PageSurface = styled.div`
  position: relative;
  padding: 8px 0 12px;

  &:before {
    content: "";
    position: absolute;
    inset: 0 0 auto;
    height: 300px;
    border-radius: 30px;
    background: radial-gradient(
        circle at top left,
        rgba(14, 165, 233, 0.16),
        transparent 38%
      ),
      radial-gradient(
        circle at top right,
        rgba(34, 197, 94, 0.12),
        transparent 32%
      ),
      linear-gradient(135deg, #f8fbff 0%, #eef6ff 50%, #f8fafc 100%);
    z-index: 0;
  }
`;

const HeroCard = styled(MuiCard)`
  position: relative;
  z-index: 1;
  overflow: hidden;
  border-radius: 28px;
  border: 1px solid rgba(148, 163, 184, 0.18);
  box-shadow: 0 28px 60px rgba(15, 23, 42, 0.12);
  background: linear-gradient(135deg, #0f172a 0%, #142b47 52%, #15416d 100%);
`;

const HeroContent = styled(MuiCardContent)`
  position: relative;
  padding: 30px 32px;

  &:last-child {
    padding-bottom: 30px;
  }

  &:before,
  &:after {
    content: "";
    position: absolute;
    border-radius: 999px;
    pointer-events: none;
  }

  &:before {
    width: 220px;
    height: 220px;
    right: -40px;
    top: -90px;
    background: rgba(56, 189, 248, 0.18);
  }

  &:after {
    width: 160px;
    height: 160px;
    right: 90px;
    bottom: -100px;
    background: rgba(34, 197, 94, 0.14);
  }
`;

const Eyebrow = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 10px 14px;
  border-radius: 999px;
  background: rgba(255, 255, 255, 0.1);
  color: rgba(255, 255, 255, 0.82);
  font-size: 0.76rem;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  backdrop-filter: blur(10px);
`;

const HeroTitle = styled(MuiTypography)`
  color: #f8fafc;
  font-weight: 800;
  letter-spacing: -0.04em;
  line-height: 1.08;
`;

const HeroText = styled(MuiTypography)`
  max-width: 720px;
  color: rgba(226, 232, 240, 0.9);
  line-height: 1.7;
`;

const HeroStatRow = styled.div`
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 14px;
  margin-top: 22px;

  @media (max-width: 960px) {
    grid-template-columns: 1fr;
  }
`;

const HeroStat = styled.div`
  padding: 18px;
  border-radius: 18px;
  background: rgba(255, 255, 255, 0.08);
  border: 1px solid rgba(255, 255, 255, 0.1);
  backdrop-filter: blur(10px);

  strong {
    display: block;
    margin-top: 8px;
    color: #ffffff;
    font-size: 1rem;
    font-weight: 800;
  }

  span {
    color: rgba(226, 232, 240, 0.82);
    font-size: 0.88rem;
  }
`;

const SectionCard = styled(MuiCard)`
  position: relative;
  z-index: 1;
  margin-top: 28px;
  border-radius: 26px;
  border: 1px solid rgba(148, 163, 184, 0.14);
  box-shadow: 0 24px 50px rgba(15, 23, 42, 0.08);
  overflow: hidden;
`;

const SectionContent = styled(MuiCardContent)`
  padding: 0;

  &:last-child {
    padding-bottom: 0;
  }
`;

const SectionHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  padding: 24px 28px 18px;
  background: linear-gradient(180deg, #ffffff 0%, #f8fbff 100%);
  border-bottom: 1px solid rgba(226, 232, 240, 0.8);

  @media (max-width: 720px) {
    flex-direction: column;
    align-items: flex-start;
  }
`;

const SectionTitle = styled(MuiTypography)`
  color: #0f172a;
  font-weight: 800;
  letter-spacing: -0.03em;
`;

const SectionText = styled(MuiTypography)`
  color: #64748b;
  max-width: 620px;
`;

const SectionPill = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 10px 14px;
  border-radius: 999px;
  background: #eff6ff;
  color: #0369a1;
  font-weight: 700;
  white-space: nowrap;
`;

const InnerContent = styled.div`
  padding: 28px;
`;

const CardGrid = styled.div`
  display: grid;
  grid-template-columns: 240px 1fr;
  gap: 28px;

  @media (max-width: 960px) {
    grid-template-columns: 1fr;
  }
`;

const ProfileSide = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: flex-start;
  padding: 24px;
  border-radius: 22px;
  background: linear-gradient(180deg, #f8fbff 0%, #ffffff 100%);
  border: 1px solid rgba(226, 232, 240, 0.9);
`;

const AvatarRing = styled.div`
  padding: 8px;
  border-radius: 999px;
  background: linear-gradient(135deg, #38bdf8 0%, #0ea5e9 45%, #22c55e 100%);
  box-shadow: 0 18px 36px rgba(14, 165, 233, 0.18);
`;

const BigAvatar = styled(Avatar)`
  width: 136px;
  height: 136px;
  border: 6px solid #ffffff;
`;

const UploadInput = styled.input`
  display: none;
`;

const UploadButton = styled(MuiButton)`
  && {
    margin-top: 18px;
    border-radius: 14px;
    padding: 10px 16px;
    text-transform: none;
    font-weight: 700;
    box-shadow: none;
  }
`;

const FormControl = styled(MuiFormControl)`
  width: 100%;
  margin-top: 8px;
  margin-bottom: 8px;
`;

const TextField = styled(MuiTextField)`
  && {
    margin-top: 8px;
    margin-bottom: 8px;
  }
`;

const SubmitButton = styled(MuiButton)`
  && {
    margin-top: 16px;
    border-radius: 14px;
    padding: 12px 18px;
    text-transform: none;
    font-weight: 700;
    box-shadow: 0 16px 30px rgba(37, 99, 235, 0.18);
  }
`;

const InfoStrip = styled.div`
  margin-top: 16px;
  padding: 12px 14px;
  border-radius: 16px;
  background: #eff6ff;
  color: #1d4ed8;
  font-size: 0.9rem;
  font-weight: 600;
  text-align: center;
`;

function Private() {
  const localStg = JSON.parse(sessionStorage.getItem("token_store"));
  const [name, setName] = useState(localStg.name);
  const [lastname, setLastName] = useState(localStg.lastName);
  const [phone, setPhone] = useState(localStg.phoneNumber);
  const [celular, setCelular] = useState(localStg.mobileNumber);
  const [streetAddress, setStreetAddress] = useState(localStg.streetAddress);
  const [preview, setPreview] = useState(
    sessionStorage.getItem("preview-profile-image") || localStg.profilePic || ""
  );

  const phoneRegexp = /^(?:[+][0-9]{2}\s?[0-9]{3}[-]?[0-9]{3,}|(?:[(][0-9]{3}[)]|[0-9]{3})\s*[-]?\s*[0-9]{3}[-][0-9]{4})(?:\s*x\s*[0-9]+)?/;

  const updateProfile = () => {
    const photo = preview || localStg.profilePic || "";
    const temp = {
      firstName: name,
      lastName: lastname,
      phoneNumber: phone,
      mobileNumber: celular,
      streetAddress,
      email: localStg.email,
      photo,
    };

    updateInfoProfile(temp)
      .then((response) => {
        if (
          response.status === 200 ||
          response.status === 202 ||
          response.status === 201
        ) {
          localStg.name = temp.firstName;
          localStg.lastName = temp.lastName;
          localStg.phoneNumber = temp.phoneNumber;
          localStg.mobileNumber = temp.mobileNumber;
          localStg.streetAddress = temp.streetAddress;
          localStg.profilePic = temp.photo;
          sessionStorage.setItem("token_store", JSON.stringify(localStg));
          Swal.fire("Exitosamente!", "Perfil actualizado!", "success");
        } else {
          Swal.fire("Error!", "Perfil no actualizado!", "error");
        }
      })
      .catch((error) => {
        Swal.fire("Error!", "Perfil no actualizado!", "error");
        console.log(error);
      });
  };

  const handleImageChange = (event) => {
    const file = event.target.files && event.target.files[0];
    if (!file) {
      return;
    }

    const reader = new FileReader();
    reader.addEventListener("load", () => {
      sessionStorage.setItem("preview-profile-image", reader.result);
      setPreview(reader.result);
    });
    reader.readAsDataURL(file);
  };

  return (
    <SectionCard>
      <SectionContent>
        <SectionHeader>
          <Box>
            <SectionTitle variant="h4">Perfil personal</SectionTitle>
            <SectionText variant="body2">
              Actualiza tu informacion visible, canales de contacto y datos de
              negocio desde una vista mas ordenada.
            </SectionText>
          </Box>
          <SectionPill>
            <Shield size={16} />
            Edicion segura
          </SectionPill>
        </SectionHeader>
        <InnerContent>
          <CardGrid>
            <ProfileSide>
              <AvatarRing>
                <BigAvatar
                  alt={localStg.name}
                  src={preview || localStg.profilePic || ""}
                />
              </AvatarRing>
              <MuiTypography variant="h6" style={{ marginTop: 16 }}>
                {`${localStg.name} ${localStg.lastName}`}
              </MuiTypography>
              <MuiTypography variant="body2" color="textSecondary">
                {localStg.email}
              </MuiTypography>
              <label htmlFor="profile-upload">
                <UploadInput
                  accept="image/*"
                  id="profile-upload"
                  type="file"
                  onChange={handleImageChange}
                />
                <UploadButton
                  variant="contained"
                  color="primary"
                  component="span"
                  startIcon={<MuiCloudUpload />}
                >
                  Cambiar imagen
                </UploadButton>
              </label>
              <InfoStrip>
                Tu foto se actualiza junto con el resto del perfil.
              </InfoStrip>
            </ProfileSide>

            <div>
              <Grid container spacing={4}>
                <Grid item xs={12} md={6}>
                  <TextField
                    id="first-name"
                    label="Nombre"
                    variant="outlined"
                    value={name}
                    onChange={(event) => setName(event.target.value)}
                    fullWidth
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    id="last-name"
                    label="Apellido"
                    variant="outlined"
                    value={lastname}
                    onChange={(event) => setLastName(event.target.value)}
                    fullWidth
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    id="email"
                    label="Correo electronico"
                    variant="outlined"
                    type="email"
                    value={localStg.email}
                    fullWidth
                    disabled
                    inputProps={{ readOnly: true }}
                  />
                </Grid>
                <Grid item xs={12} md={3}>
                  <TextField
                    id="phone"
                    label="Telefono"
                    variant="outlined"
                    value={phone}
                    onChange={(event) => {
                      if (
                        event.target.value === "" ||
                        phoneRegexp.test(event.target.value)
                      ) {
                        setPhone(event.target.value);
                      }
                    }}
                    fullWidth
                  />
                </Grid>
                <Grid item xs={12} md={3}>
                  <TextField
                    id="mobile"
                    label="Celular"
                    variant="outlined"
                    value={celular}
                    onChange={(event) => {
                      if (
                        event.target.value === "" ||
                        phoneRegexp.test(event.target.value)
                      ) {
                        setCelular(event.target.value);
                      }
                    }}
                    fullWidth
                  />
                </Grid>
                <Grid item xs={12} md={4}>
                  <TextField
                    id="identification"
                    label="ID"
                    variant="outlined"
                    value={localStg.identification}
                    fullWidth
                    disabled
                    inputProps={{ readOnly: true }}
                  />
                </Grid>
                <Grid item xs={12} md={8}>
                  <TextField
                    id="address"
                    label="Direccion"
                    variant="outlined"
                    value={streetAddress}
                    onChange={(event) => setStreetAddress(event.target.value)}
                    fullWidth
                  />
                </Grid>
                <Grid item xs={12} md={4}>
                  <TextField
                    id="business-type"
                    label="Tipo de Negocio"
                    variant="outlined"
                    value={localStg.businessType}
                    fullWidth
                    disabled
                    inputProps={{ readOnly: true }}
                  />
                </Grid>
                <Grid item xs={12} md={4}>
                  <TextField
                    id="company-name"
                    label="Compania"
                    variant="outlined"
                    value={localStg.company}
                    fullWidth
                    disabled
                    inputProps={{ readOnly: true }}
                  />
                </Grid>
                <Grid item xs={12} md={4}>
                  <FormControl variant="outlined">
                    <InputLabel id="countryLabel">Pais</InputLabel>
                    <Select
                      labelId="countryLabel"
                      id="country"
                      label="Pais"
                      disabled
                      value={localStg.country}
                      inputProps={{ readOnly: true }}
                    >
                      <MenuItem value={localStg.country}>
                        {localStg.country}
                      </MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} md={4}>
                  <TextField
                    id="city"
                    label="Ciudad"
                    variant="outlined"
                    value={localStg.city}
                    fullWidth
                    disabled
                    inputProps={{ readOnly: true }}
                  />
                </Grid>
              </Grid>

              <SubmitButton
                variant="contained"
                color="primary"
                onClick={updateProfile}
              >
                Guardar cambios
              </SubmitButton>
            </div>
          </CardGrid>
        </InnerContent>
      </SectionContent>
    </SectionCard>
  );
}

function BankInfo() {
  const [nameBankReferral, setBankNameReferral] = useState("");
  const [bankNumberReferral, setBankAccountNumberReferral] = useState("");

  useEffect(() => {
    getClientBankInfo()
      .then((response) => {
        if (response.data.length > 0) {
          response.data.forEach((element) => {
            if (element.originSystem === "REFERRAL") {
              setBankNameReferral(element.bank.name);
              setBankAccountNumberReferral(element.bankAccountNumber);
            }
          });
        }
      })
      .catch((error) => {
        console.error(error);
      });
  }, []);

  return (
    <SectionCard>
      <SectionContent>
        <SectionHeader>
          <Box>
            <SectionTitle variant="h4">Informacion bancaria</SectionTitle>
            <SectionText variant="body2">
              Consulta la cuenta vinculada para pagos de referidos y confirma el
              banco asociado a tu perfil.
            </SectionText>
          </Box>
          <SectionPill>
            <CreditCard size={16} />
            Datos de liquidacion
          </SectionPill>
        </SectionHeader>
        <InnerContent>
          <Grid container spacing={4}>
            <Grid item xs={12} md={6}>
              <TextField
                id="bank-name-referral"
                label="Nombre del Banco"
                disabled
                variant="outlined"
                value={nameBankReferral}
                fullWidth
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                id="bank-account-number-referral"
                label="No. Cuenta"
                disabled
                variant="outlined"
                value={bankNumberReferral}
                fullWidth
                inputProps={{ readOnly: true }}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
          </Grid>
        </InnerContent>
      </SectionContent>
    </SectionCard>
  );
}

function Settings() {
  return (
    <React.Fragment>
      <Helmet title="Settings" />
      <PageSurface>
        <HeroCard>
          <HeroContent>
            <Eyebrow>
              <SettingsIcon size={14} />
              Centro de configuracion
            </Eyebrow>
            <HeroTitle variant="h2" mt={4} mb={2}>
              Configuraciones
            </HeroTitle>
            <HeroText variant="body1">
              Gestiona tu perfil, revisa la cuenta bancaria vinculada y mantén
              actualizada la informacion principal de tu cuenta en una interfaz
              mas limpia y contemporanea.
            </HeroText>
            <HeroStatRow>
              <HeroStat>
                <span>Perfil personal</span>
                <strong>Edicion centralizada</strong>
              </HeroStat>
              <HeroStat>
                <span>Seguridad de cuenta</span>
                <strong>Datos protegidos</strong>
              </HeroStat>
              <HeroStat>
                <span>Pago bancario</span>
                <strong>Revision rapida</strong>
              </HeroStat>
            </HeroStatRow>
          </HeroContent>
        </HeroCard>

        <Grid container spacing={0}>
          <Grid item xs={12}>
            <Private />
            <LocalizationPreferences />
            <BankInfo />
          </Grid>
        </Grid>
      </PageSurface>
    </React.Fragment>
  );
}

export default Settings;
